import { Scalars, TimeWindow } from "@api/graphql/generated/generated-types";
import { dayAbbreviations } from "@src/orders/create/utils/DateConstants";
import moment, { Moment } from "moment-timezone";
import { momentToTimePickerDate } from "@src/common/utils/timezoneConverters";

export function currentEpochSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

export function minutesAfterCurrentEpochSeconds(minutes: number): number {
  return currentEpochSeconds() + minutes * 60;
}

export function makeDateFromEpochSeconds(epochSeconds: number): Date {
  return new Date(epochSeconds * 1000);
}

export function makeMomentFromDate(date: Date): Moment {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return moment(`${year}-${month}-${day} ${hours}:${minutes}`);
}

export const DATE_FORMAT = "MMM Do, YYYY";
export const DATE_FORMAT_WITH_DAY = "ddd, MMM Do";
export const DATE_TIME_FORMAT = "LT, MMM Do";
export const TIME_FORMAT = "LT";
export const SHORT_DATE_FORMAT = "MMM D";

/**
 * Helper to convert string or Date to Date object
 * Handles ISO date strings from GraphQL (e.g., "2025-12-10")
 * Uses T00:00:00 suffix to parse in local timezone, not UTC
 */
function toDateObject(date: Date | string): Date {
  if (typeof date === "string") {
    // If it's just a date string (YYYY-MM-DD), add time to parse in local TZ
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return new Date(date + "T00:00:00");
    }
    return new Date(date);
  }
  return date;
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) {
    return "";
  }
  const dateObj = toDateObject(date);
  // Use native toLocaleDateString to display in browser's local timezone
  // Avoids moment's courier TZ interpretation which shifts dates for remote workers
  return dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateWithDay(
  date: Date | string | null | undefined
): string {
  if (!date) {
    return "";
  }
  const dateObj = toDateObject(date);
  // Use native toLocaleDateString to display in browser's local timezone
  // Avoids moment's courier TZ interpretation which shifts dates for remote workers
  return dateObj.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) {
    return "";
  }
  const dateObj = toDateObject(date);
  // Use native toLocaleString to display in browser's local timezone
  // Avoids moment's courier TZ interpretation which shifts dates for remote workers
  return dateObj.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    month: "short",
    day: "numeric",
  });
}

export function formatTime(
  date: Date | string | undefined,
  format?: string
): string {
  if (!date) {
    return "";
  }
  const dateObj = toDateObject(date);
  // Use native methods to display in browser's local timezone
  // Avoids moment's courier TZ interpretation which shifts times for remote workers
  if (format && format.includes("MMM")) {
    // Format includes date parts (e.g., "LT, MMM Do") - include date in output
    return dateObj.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      month: "short",
      day: "numeric",
    });
  }
  return dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function isToday(input: Date | string | null | undefined): boolean {
  if (!input) return false;

  // For ISO date strings (YYYY-MM-DD), compare directly with courier's today
  // This avoids timezone issues from parsing the string as a Date
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return moment().format("YYYY-MM-DD") === input;
  }

  // For Date objects (from DatePicker), compare visual calendar dates
  // inputDate is in browser TZ, courierToday is in courier TZ
  // We compare the calendar day number which should be the same if it's "today"
  const inputDate = typeof input === "string" ? new Date(input) : input;
  const courierToday = moment();
  return (
    inputDate.getFullYear() === courierToday.year() &&
    inputDate.getMonth() === courierToday.month() &&
    inputDate.getDate() === courierToday.date()
  );
}

export function isDateStringToday(input: Scalars["Date"]) {
  // Use moment() directly to get today in courier TZ
  return moment().format("YYYY-MM-DD") === input;
}

export function formatTimestamp(timestamp: number): string {
  if (!timestamp) {
    return "";
  }
  // Convert the timestamp to a Date object
  const date = new Date(timestamp * 1000);

  // Format the date object to "hh:mm A" format
  const formattedTime = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return formattedTime;
}

export function formatTimeWindow(timeWindow: TimeWindow): string {
  const { open, close } = timeWindow;

  if (open && close) {
    return `${formatTimestamp(open)} - ${formatTimestamp(close)}`;
  }

  if (open && !close) {
    return `Ready by ${formatTimestamp(open)}`;
  }

  if (!open && close) {
    return `Due at ${formatTimestamp(close)}`;
  }

  return "Ready now";
}

const SECONDS_IN_DAY = 86400;

export function getSecondsSinceMidnightUtc(isoDateTime) {
  const date = new Date(isoDateTime);
  let secondsSinceMidnightUtc =
    date.getUTCHours() * 3600 + // Hours to seconds
    date.getUTCMinutes() * 60 + // Minutes to seconds
    date.getUTCSeconds(); // Seconds

  const localDay = date.getDate();
  const utcDay = date.getUTCDate();

  if (localDay !== utcDay) {
    if (date.getTimezoneOffset() > 0) {
      secondsSinceMidnightUtc += SECONDS_IN_DAY;
    } else {
      secondsSinceMidnightUtc -= SECONDS_IN_DAY;
    }
  }

  return secondsSinceMidnightUtc;
}

export function incrementDays(date: Date, days: number): Date {
  let newDate = new Date(date.getTime());
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

export function isSameDay(date1, date2) {
  if (date1 === undefined || date2 === undefined) {
    return false;
  }
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export const formatWeekDays = (days: string[]) =>
  days.map((day) => dayAbbreviations[day] || day).join(", ");

export function getExactMomentForSecondsSinceMidnightUtc(
  secondsSinceMidnightUtc: number
): Date {
  const today = moment().utc().startOf("day");
  const exactMoment = today.add(secondsSinceMidnightUtc, "seconds");
  return exactMoment.toDate();
}

export const formatTimeFromSecondsSinceMidnight = (
  secondsSinceMidnight: number
): string => {
  const time = moment().startOf("day").add(secondsSinceMidnight, "seconds");
  return time.format("LT");
};

export const convertSecondsToDateTime = (secondsSinceMidnight: number): Date =>
  momentToTimePickerDate(
    moment().startOf("day").add(secondsSinceMidnight, "seconds")
  );

export function timeStringToSeconds(timeString: string): number {
  const time = moment(timeString, "HH:mm");
  const secondsSinceMidnight = time.hours() * 3600 + time.minutes() * 60;
  return secondsSinceMidnight;
}

export function formatSecondsToTimeString(
  seconds: number | null | undefined
): string | undefined {
  if (seconds == null) return undefined;
  return moment.utc(seconds * 1000).format("HH:mm");
}

export function convertToSecondsSinceMidnight(time: Date): number {
  // Use native Date methods to avoid moment's courier TZ interpretation
  const secondsSinceMidnight =
    time.getHours() * 3600 + time.getMinutes() * 60 + time.getSeconds();

  return secondsSinceMidnight;
}

export function getSecondsSinceMidnight(isoDateTime) {
  const date = new Date(isoDateTime);
  return convertToSecondsSinceMidnight(date);
}

export function formatDateShort(
  date: Date | string | null | undefined
): string {
  if (!date) {
    return "";
  }
  const dateObj = toDateObject(date);
  // Use native toLocaleDateString to display in browser's local timezone
  return dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
